import { useState, type FormEvent } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type FamilyWithDownloads,
  type Membership,
  type Member,
  type ChildProfile,
  type PendingInvite,
  type CreatedInvite,
  useAccessGroups,
  useAccessGroupMembers,
  useSetAccessGroupMembers,
} from '@wovendays/api-client';
import { useTranslation } from '@wovendays/i18n';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth';
import { formatAge } from '@/utils/age';
import { Tile } from '@/components/ui/tile';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Pencil, X, ArrowLeft, Shield, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface FamilyResponse {
  family: FamilyWithDownloads;
  membership: Membership;
}

export function FamilySettingsPage() {
  const { t } = useTranslation('settings');
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: familyData, isLoading: familyLoading, isError: familyError, refetch: refetchFamily } = useQuery({
    queryKey: ['family', id],
    queryFn: () => apiClient.get<FamilyResponse>(`/api/families/${id}`),
    enabled: !!id,
  });

  const { data: membersData, isLoading: membersLoading, isError: membersError, refetch: refetchMembers } = useQuery({
    queryKey: ['family', id, 'members'],
    queryFn: () => apiClient.get<{ members: Member[] }>(`/api/families/${id}/members`),
    enabled: !!id,
  });

  const { data: childrenData, isLoading: childrenLoading, refetch: refetchChildren } = useQuery({
    queryKey: ['children', id],
    queryFn: () => apiClient.get<{ children: ChildProfile[] }>(`/api/families/${id}/children`),
    enabled: !!id,
  });

  const leaveMutation = useMutation({
    mutationFn: () => apiClient.post(`/api/families/${id}/leave`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      navigate('/');
    },
  });

  const deleteFamilyMutation = useMutation({
    mutationFn: () => apiClient.delete(`/api/families/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      navigate('/');
    },
  });

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  if (familyLoading || membersLoading || childrenLoading) {
    return (
      <div data-testid="family-settings-loading" className="flex items-center justify-center py-12" role="status" aria-label={t('title')}>
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-muted border-t-primary-600" />
      </div>
    );
  }

  if (familyError || membersError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-foreground">{t('loadFailed')}</p>
        <Button
          onClick={() => { refetchFamily(); refetchMembers(); refetchChildren(); }}
          className="mt-3"
          size="sm"
        >
          {t('common:retry')}
        </Button>
      </div>
    );
  }

  const family = familyData?.family;
  const members = membersData?.members ?? [];
  const childProfiles = childrenData?.children ?? [];
  const currentMember = members.find((m) => m.userId === currentUser?.id);
  const isOwner = currentMember?.role === 'owner';
  const isAdmin = currentMember?.role === 'admin';
  const canManage = isOwner || isAdmin;

  if (!family) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('notFound')}</p>
        <Link to="/" className="mt-2 inline-block text-sm text-primary-600 hover:text-primary-500">
          {t('backToFamilies')}
        </Link>
      </div>
    );
  }

  const createdYear = new Date(family.createdAt).getFullYear();

  return (
    <div>
      <div className="mb-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('common:back')}
        </Link>
      </div>

      <div className="grid gap-2">
        {/* Hero tile */}
        <FamilyNameHeader familyId={family.id} name={family.name} year={createdYear} canEdit={canManage} />

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          <MembersStat members={members} />
          <ChildrenStat children={childProfiles} />
          <PhotosStat children={childProfiles} />
        </div>

        {/* Children + Invites — side-by-side on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <ChildrenSection
            familyId={family.id}
            children={childProfiles}
            canManage={canManage}
            members={members}
          />
          {canManage && <InviteSection familyId={family.id} />}
        </div>

        {/* Members management */}
        <MembersSection
          familyId={family.id}
          members={members}
          currentMember={currentMember}
          isOwner={isOwner}
          isAdmin={isAdmin}
        />

        {/* Upload Permissions — owner/admin only */}
        {canManage && family && (
          <UploadPermissionsSection
            familyId={family.id}
            minUploadRole={family.minUploadRole}
            members={members}
          />
        )}

        {/* Download Permissions — owner/admin only */}
        {canManage && family && (
          <DownloadPermissionsSection
            familyId={family.id}
            allowDownloads={family.allowDownloads}
            members={members}
          />
        )}

        {/* Full Access Group — owner/admin only */}
        {canManage && family && (
          <FullAccessSection
            familyId={family.id}
            members={members}
          />
        )}

        {/* Leave family */}
        {!isOwner && currentMember && (
          <Tile variant="cream" className="p-4">
            <button
              onClick={() => {
                if (confirm(t('leaveFamily.confirm'))) {
                  leaveMutation.mutate();
                }
              }}
              disabled={leaveMutation.isPending}
              className="font-mono text-xs uppercase tracking-wide text-destructive hover:text-destructive/80 transition-colors"
            >
              {leaveMutation.isPending ? t('leaveFamily.leaving') : t('leaveFamily.title')}
            </button>
            {leaveMutation.isError && (
              <p className="mt-2 text-sm text-destructive">{t('leaveFamily.failed')}</p>
            )}
          </Tile>
        )}

        {/* Delete family — owner only */}
        {isOwner && currentMember && (
          <Tile variant="cream" className="border border-destructive/20 p-4">
            <h3 className="font-mono text-xs uppercase tracking-wide text-destructive mb-2">{t('deleteFamily.dangerZone')}</h3>
            <p className="text-sm text-muted-foreground mb-3">
              {t('deleteFamily.description')}
            </p>
            <button
              onClick={() => { setDeleteConfirmText(''); setShowDeleteDialog(true); }}
              className="font-mono text-xs uppercase tracking-wide text-destructive hover:text-destructive/80 transition-colors"
            >
              {t('deleteFamily.deleteFamily')}
            </button>
            {deleteFamilyMutation.isError && (
              <p className="mt-2 text-sm text-destructive">{t('deleteFamily.failed')}</p>
            )}

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('deleteFamily.confirmTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('deleteFamily.description')}
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <label htmlFor="delete-confirm" className="block text-sm text-foreground mb-2">
                    Type <span className="font-semibold">{family.name}</span> to confirm
                  </label>
                  <Input
                    id="delete-confirm"
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={family.name}
                    autoFocus
                  />
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>
                    {t('common:cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={deleteConfirmText !== family.name || deleteFamilyMutation.isPending}
                    onClick={() => deleteFamilyMutation.mutate()}
                  >
                    {deleteFamilyMutation.isPending ? t('common:deleting') : t('deleteFamily.deleteFamily')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Tile>
        )}
      </div>
    </div>
  );
}

function FamilyNameHeader({ familyId, name, year, canEdit }: { familyId: string; name: string; year: number; canEdit: boolean }) {
  const { t } = useTranslation('settings');
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (newName: string) =>
      apiClient.put(`/api/families/${familyId}`, { name: newName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
      setIsEditing(false);
    },
  });

  if (isEditing) {
    return (
      <Tile variant="dark" className="p-6">
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="flex-1 text-xl font-serif bg-white/10 border-white/20 text-white"
            autoFocus
            aria-label={t('familyName.label')}
          />
          <Button
            onClick={() => {
              if (editName.trim()) mutation.mutate(editName.trim());
            }}
            disabled={mutation.isPending || !editName.trim()}
            size="sm"
          >
            {t('common:save')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setEditName(name); setIsEditing(false); }}
            className="text-zinc-400 hover:text-white"
          >
            {t('common:cancel')}
          </Button>
        </div>
      </Tile>
    );
  }

  return (
    <Tile variant="dark" className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl text-white">{name}</h2>
          <p className="mt-1 font-mono text-xs text-zinc-400 uppercase tracking-wide">{t('established', { year })}</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditName(name); setIsEditing(true); }}
            className="text-zinc-400 hover:text-white transition-colors"
            aria-label={t('familyName.editTitle')}
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
    </Tile>
  );
}

function MembersStat({ members }: { members: Member[] }) {
  const { t } = useTranslation('settings');
  const avatarColors = ['bg-primary-500', 'bg-amber-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500'];
  return (
    <Tile variant="cream" className="p-4">
      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1">{t('stats.members')}</p>
      <p className="text-2xl font-bold text-foreground">{members.length}</p>
      <div className="mt-2 flex -space-x-1.5">
        {members.slice(0, 5).map((m, i) => (
          <div
            key={m.id}
            className={`flex h-6 w-6 items-center justify-center rounded-full text-white text-[10px] font-semibold ring-2 ring-[#faf8f5] dark:ring-zinc-800 ${avatarColors[i % avatarColors.length]}`}
            title={m.user.name}
          >
            {m.user.name.charAt(0).toUpperCase()}
          </div>
        ))}
        {members.length > 5 && (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-[#faf8f5] dark:ring-zinc-800">
            +{members.length - 5}
          </div>
        )}
      </div>
    </Tile>
  );
}

function ChildrenStat({ children }: { children: ChildProfile[] }) {
  const { t } = useTranslation('settings');
  const avatarColors = ['bg-primary-500', 'bg-amber-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500'];
  return (
    <Tile variant="cream" className="p-4">
      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1">{t('stats.children')}</p>
      <p className="text-2xl font-bold text-foreground">{children.length}</p>
      {children.length > 0 && (
        <div className="mt-2 flex -space-x-1.5">
          {children.slice(0, 5).map((c, i) => (
            <div
              key={c.id}
              className={`flex h-6 w-6 items-center justify-center rounded-full text-white text-[10px] font-semibold ring-2 ring-[#faf8f5] dark:ring-zinc-800 ${avatarColors[i % avatarColors.length]}`}
              title={c.name}
            >
              {c.name.charAt(0).toUpperCase()}
            </div>
          ))}
        </div>
      )}
    </Tile>
  );
}

function PhotosStat({ children }: { children: ChildProfile[] }) {
  const { t } = useTranslation('settings');
  const totalPhotos = children.reduce((sum, c) => sum + c.photoCount, 0);
  return (
    <Tile variant="cream" className="p-4">
      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1">{t('stats.photos')}</p>
      <p className="text-2xl font-bold text-foreground">{totalPhotos}</p>
    </Tile>
  );
}

function formatLastSeen(timestamp: number | null, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!timestamp) return t('common:time.never');
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('common:time.justNow');
  if (minutes < 60) return t('common:time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('common:time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('common:time.daysAgo', { count: days });
  if (days < 30) return t('common:time.weeksAgo', { count: Math.floor(days / 7) });
  return new Date(timestamp).toLocaleDateString();
}

function FamilyRoleEditor({
  currentRole,
  onSave,
  isPending,
}: {
  currentRole: string | null;
  onSave: (familyRole: string | null) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation('settings');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentRole || '');

  if (!editing) {
    return (
      <button
        onClick={() => { setDraft(currentRole || ''); setEditing(true); }}
        className="font-mono text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
      >
        {currentRole ? t('members.editLabel') : t('members.addLabel')}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 mt-0.5">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={t('members.labelPlaceholder')}
        maxLength={50}
        className="h-6 w-28 rounded border bg-background px-1.5 text-xs"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSave(draft.trim() || null);
            setEditing(false);
          }
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <button
        onClick={() => { onSave(draft.trim() || null); setEditing(false); }}
        disabled={isPending}
        className="text-xs text-primary-600 hover:text-primary-700"
      >
        {t('common:save')}
      </button>
      <button
        onClick={() => setEditing(false)}
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        {t('common:cancel')}
      </button>
    </div>
  );
}

function MembersSection({
  familyId,
  members,
  currentMember,
  isOwner,
  isAdmin,
}: {
  familyId: string;
  members: Member[];
  currentMember: Member | undefined;
  isOwner: boolean;
  isAdmin: boolean;
}) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const roleChangeMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      apiClient.put(`/api/families/${familyId}/members/${memberId}`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'members'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) =>
      apiClient.delete(`/api/families/${familyId}/members/${memberId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'members'] });
    },
  });

  const familyRoleMutation = useMutation({
    mutationFn: ({ memberId, familyRole }: { memberId: string; familyRole: string | null }) =>
      apiClient.put(`/api/families/${familyId}/members/${memberId}`, { familyRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'members'] });
    },
  });

  const transferMutation = useMutation({
    mutationFn: (newOwnerId: string) =>
      apiClient.post(`/api/families/${familyId}/transfer-ownership`, { newOwnerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'members'] });
    },
  });

  function canChangeRole(member: Member): boolean {
    if (member.role === 'owner') return false;
    if (member.userId === currentMember?.userId) return false;
    if (isOwner) return true;
    if (isAdmin && member.role !== 'admin') return true;
    return false;
  }

  function canRemove(member: Member): boolean {
    if (member.role === 'owner') return false;
    if (member.userId === currentMember?.userId) return false;
    if (isOwner) return true;
    if (isAdmin && member.role !== 'admin') return true;
    return false;
  }

  function getRoleOptions(member: Member): string[] {
    if (isOwner) return ['viewer', 'contributor', 'admin'];
    if (isAdmin && member.role !== 'admin') return ['viewer', 'contributor'];
    return [];
  }

  const nonOwnerMembers = members.filter(m => m.role !== 'owner');
  const avatarColors = ['bg-primary-500', 'bg-amber-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500'];

  const canManageAny = isOwner || isAdmin;

  return (
    <Tile variant="white" className="p-5">
      <h3 className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
        {t('members.title', { count: members.length })}
      </h3>

      <div className="space-y-2">
        {members.map((member, index) => (
          <div key={member.id} className="flex items-center justify-between rounded-xl bg-[#faf8f5] dark:bg-zinc-800/50 p-3">
            <div className="flex items-center gap-3">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white text-sm font-semibold ${avatarColors[index % avatarColors.length]}`}>
                {member.user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium text-foreground">{member.user.name}</p>
                  {member.familyRole && (
                    <span className="text-xs text-muted-foreground italic">{member.familyRole}</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{member.user.email}</p>
                <p className="font-mono text-xs text-muted-foreground/70">{t('members.lastSeen', { time: formatLastSeen(member.user.lastSeenAt, t) })}</p>
                {(canManageAny || member.userId === currentMember?.userId) && (
                  <FamilyRoleEditor
                    currentRole={member.familyRole}
                    onSave={(familyRole) => familyRoleMutation.mutate({ memberId: member.id, familyRole })}
                    isPending={familyRoleMutation.isPending}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canChangeRole(member) ? (
                <Select
                  value={member.role}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    if (confirm(t('members.confirmRoleChange', { name: member.user.name, role: newRole }))) {
                      roleChangeMutation.mutate({ memberId: member.id, role: newRole });
                    } else {
                      e.target.value = member.role;
                    }
                  }}
                  className="h-8 text-xs"
                  aria-label={t('members.manageRole')}
                >
                  {getRoleOptions(member).map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </Select>
              ) : (
                <Badge variant="secondary">{member.role}</Badge>
              )}
              {canRemove(member) && (
                <button
                  onClick={() => {
                    if (confirm(t('members.confirmRemove', { name: member.user.name }))) {
                      removeMutation.mutate(member.id);
                    }
                  }}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`${t('common:remove')} ${member.user.name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}

        {canManageAny && isOwner && nonOwnerMembers.length > 0 && (
          <button
            onClick={() => {
              let target: Member | undefined;

              if (nonOwnerMembers.length === 1) {
                target = nonOwnerMembers[0];
              } else {
                const inputName = prompt(`${t('members.transferPrompt')}\n${nonOwnerMembers.map(m => `  ${m.user.name}`).join('\n')}`);
                if (!inputName) return;
                target = nonOwnerMembers.find(m => m.user.name === inputName);
                if (!target) { alert(t('members.memberNotFound')); return; }
              }

              if (!target) return;

              if (confirm(t('members.confirmTransferLong', { name: target.user.name }))) {
                transferMutation.mutate(target.userId);
              }
            }}
            className="font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('members.transferOwnership')}
          </button>
        )}
      </div>
    </Tile>
  );
}

function ChildrenSection({
  familyId,
  children: childProfiles,
  canManage,
  members,
}: {
  familyId: string;
  children: ChildProfile[];
  canManage: boolean;
  members: Member[];
}) {
  const { t } = useTranslation('settings');
  const [showForm, setShowForm] = useState(false);
  const [editingChildId, setEditingChildId] = useState<string | null>(null);
  const [permissionsChildId, setPermissionsChildId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editBirthDate, setEditBirthDate] = useState('');
  const queryClient = useQueryClient();

  const editChildMutation = useMutation({
    mutationFn: ({ childId, name, birthDate }: { childId: string; name?: string; birthDate?: number }) =>
      apiClient.put(`/api/families/${familyId}/children/${childId}`, { name, birthDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children', familyId] });
      setEditingChildId(null);
    },
  });

  function startEditing(child: ChildProfile) {
    setEditingChildId(child.id);
    setEditName(child.name);
    setEditBirthDate(new Date(child.birthDate).toISOString().split('T')[0]!);
  }

  function handleSaveEdit(childId: string) {
    const updates: { name?: string; birthDate?: number } = {};
    if (editName.trim()) updates.name = editName.trim();
    if (editBirthDate) updates.birthDate = new Date(editBirthDate).getTime();
    if (Object.keys(updates).length > 0) {
      editChildMutation.mutate({ childId, ...updates });
    }
  }

  const avatarColors = ['bg-primary-500', 'bg-amber-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500'];

  return (
    <Tile variant="cream" className="p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          {t('children.title', { count: childProfiles.length })}
        </h3>
        {canManage && !showForm && (
          <button onClick={() => setShowForm(true)} className="font-mono text-xs text-primary-600 hover:text-primary-700 transition-colors">
            {t('children.addChild')}
          </button>
        )}
      </div>

      {showForm && (
        <AddChildForm familyId={familyId} onDone={() => setShowForm(false)} />
      )}

      {childProfiles.length === 0 && !showForm ? (
        <p className="text-sm text-muted-foreground">{t('children.noChildren')}</p>
      ) : (
        <div className="space-y-2">
          {childProfiles.map((child, index) => (
            <div key={child.id}>
              {editingChildId === child.id ? (
                <div className="rounded-xl bg-background p-3 space-y-2">
                  <div>
                    <label htmlFor={`edit-child-name-${child.id}`} className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      {t('children.nameLabel')}
                    </label>
                    <Input
                      id={`edit-child-name-${child.id}`}
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label htmlFor={`edit-child-birth-${child.id}`} className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      {t('children.birthDateLabel')}
                    </label>
                    <Input
                      id={`edit-child-birth-${child.id}`}
                      type="date"
                      value={editBirthDate}
                      onChange={(e) => setEditBirthDate(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSaveEdit(child.id)}
                      disabled={editChildMutation.isPending || !editName.trim()}
                    >
                      {editChildMutation.isPending ? t('common:saving') : t('common:save')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingChildId(null)}
                    >
                      {t('common:cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white text-xs font-semibold ${avatarColors[index % avatarColors.length]}`}>
                        {child.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-foreground">{child.name}</p>
                          {child.visibilityMode === 'selected' && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{t('children.restricted')}</Badge>
                          )}
                        </div>
                        <p className="font-mono text-xs text-muted-foreground">{formatAge(child.birthDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {t('children.photo', { count: child.photoCount })}
                      </span>
                      {canManage && (
                        <>
                          <button
                            onClick={() => setPermissionsChildId(permissionsChildId === child.id ? null : child.id)}
                            className={`transition-colors ${permissionsChildId === child.id ? 'text-primary-600' : 'text-muted-foreground hover:text-foreground'}`}
                            aria-label={`${child.name}`}
                          >
                            <Shield className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => startEditing(child)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={`${t('common:edit')} ${child.name}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {permissionsChildId === child.id && (
                    <ChildPermissionsEditor
                      familyId={familyId}
                      childId={child.id}
                      childName={child.name}
                      members={members}
                      onClose={() => setPermissionsChildId(null)}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Tile>
  );
}

interface ChildPermissions {
  visibilityMode: string;
  allowedUsers: { userId: string; name: string; email: string }[];
}

function ChildPermissionsEditor({
  familyId,
  childId,
  childName,
  members,
  onClose,
}: {
  familyId: string;
  childId: string;
  childName: string;
  members: Member[];
  onClose: () => void;
}) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const { data: permData, isLoading } = useQuery({
    queryKey: ['child-permissions', childId],
    queryFn: () => apiClient.get<{ permissions: ChildPermissions }>(`/api/families/${familyId}/children/${childId}/permissions`),
  });

  const [mode, setMode] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Initialize state when data loads
  const permissions = permData?.permissions;
  if (permissions && mode === null) {
    setMode(permissions.visibilityMode);
    setSelectedUserIds(new Set(permissions.allowedUsers.map(u => u.userId)));
  }

  const mutation = useMutation({
    mutationFn: (data: { visibilityMode: string; allowedUserIds: string[] }) =>
      apiClient.put(`/api/families/${familyId}/children/${childId}/permissions`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['child-permissions', childId] });
      queryClient.invalidateQueries({ queryKey: ['children', familyId] });
      onClose();
    },
  });

  const handleSave = () => {
    mutation.mutate({
      visibilityMode: mode || 'everyone',
      allowedUserIds: mode === 'selected' ? [...selectedUserIds] : [],
    });
  };

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Non-admin/owner members are the ones that need explicit access
  const selectableMembers = members.filter(m => m.role !== 'owner' && m.role !== 'admin');
  const privilegedMembers = members.filter(m => m.role === 'owner' || m.role === 'admin');

  if (isLoading) {
    return (
      <div className="mt-2 rounded-xl bg-background p-3 text-center">
        <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary-600" />
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-xl bg-background p-3 space-y-3">
      <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
        {t('childPermissions.title', { name: childName })}
      </p>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant={mode === 'everyone' ? 'default' : 'outline'}
          onClick={() => setMode('everyone')}
        >
          {t('childPermissions.everyone')}
        </Button>
        <Button
          size="sm"
          variant={mode === 'selected' ? 'default' : 'outline'}
          onClick={() => setMode('selected')}
        >
          {t('childPermissions.selectedPeople')}
        </Button>
      </div>

      {mode === 'selected' && (
        <div className="space-y-1.5">
          {privilegedMembers.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('childPermissions.alwaysAccess', { name: privilegedMembers.map(m => m.user.name).join(', ') })}
            </p>
          )}
          {selectableMembers.map(m => (
            <label key={m.userId} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selectedUserIds.has(m.userId)}
                onChange={() => toggleUser(m.userId)}
                className="rounded border-muted-foreground"
              />
              <span>{m.user.name}</span>
              <span className="text-xs text-muted-foreground">({m.role})</span>
            </label>
          ))}
          <p className="text-xs text-muted-foreground">
            {t('childPermissions.visibleTo', { count: selectedUserIds.size + privilegedMembers.length, total: members.length })}
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" onClick={handleSave} disabled={mutation.isPending}>
          {mutation.isPending ? t('common:saving') : t('common:save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('common:cancel')}
        </Button>
      </div>
    </div>
  );
}

function AddChildForm({
  familyId,
  onDone,
}: {
  familyId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation('settings');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { name: string; birthDate: number }) =>
      apiClient.post(`/api/families/${familyId}/children`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children', familyId] });
      onDone();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('children.failedToAdd'));
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !birthDate) return;
    mutation.mutate({
      name: name.trim(),
      birthDate: new Date(birthDate).getTime(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="mb-3">
      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-3">
        <div>
          <label htmlFor="child-name" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
            {t('children.nameLabel')}
          </label>
          <Input
            id="child-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('children.namePlaceholder')}
            autoFocus
          />
        </div>
        <div>
          <label htmlFor="child-birthdate" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
            {t('children.birthDateLabel')}
          </label>
          <Input
            id="child-birthdate"
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={mutation.isPending || !name.trim() || !birthDate}
            size="sm"
          >
            {mutation.isPending ? t('common:creating') : t('common:create')}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onDone}>
            {t('common:cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function InviteSection({ familyId }: { familyId: string }) {
  const { t } = useTranslation('settings');
  const [newInvite, setNewInvite] = useState<CreatedInvite | null>(null);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const { data: invitesData } = useQuery({
    queryKey: ['family', familyId, 'invites'],
    queryFn: () => apiClient.get<{ invites: PendingInvite[] }>(`/api/families/${familyId}/invites`),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ invite: CreatedInvite }>(`/api/families/${familyId}/invites`, {
        defaultRole: 'viewer',
        expiresInDays: 7,
      }),
    onSuccess: (data) => {
      setNewInvite(data.invite);
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'invites'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (inviteId: string) =>
      apiClient.delete(`/api/families/${familyId}/invites/${inviteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'invites'] });
    },
  });

  const pendingInvites = invitesData?.invites ?? [];

  async function handleCopy() {
    if (!newInvite) return;
    try {
      await navigator.clipboard.writeText(newInvite.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  }

  function formatTimeAgo(ms: number): string {
    const days = Math.floor((Date.now() - ms) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  }

  return (
    <Tile variant="white" className="p-5">
      <h3 className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
        {t('invites.title')}
      </h3>

      {pendingInvites.length > 0 && (
        <div className="space-y-2 mb-4">
          {pendingInvites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between rounded-xl bg-[#faf8f5] dark:bg-zinc-800/50 p-3">
              <div>
                <p className="text-sm text-foreground">
                  {t('invites.roleInvite', { role: inv.defaultRole })}
                </p>
                <p className="font-mono text-xs text-muted-foreground">
                  {t('invites.createdExpires', { created: formatTimeAgo(inv.createdAt), expires: new Date(inv.expiresAt).toLocaleDateString() })}
                </p>
              </div>
              <button
                onClick={() => {
                  if (confirm(t('invites.revokeConfirm'))) {
                    revokeMutation.mutate(inv.id);
                  }
                }}
                className="text-muted-foreground hover:text-destructive transition-colors"
                aria-label={t('invites.revokeLabel')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {newInvite && (
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-2">{t('invites.shareLink')}</p>
          <div className="flex gap-2">
            <code className="flex-1 rounded-xl bg-[hsl(var(--input))] px-3 py-2 font-mono text-xs text-foreground break-all">
              {newInvite.inviteUrl}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t('common:copied') : t('common:copy')}
            </Button>
          </div>
          <p className="mt-2 font-mono text-xs text-muted-foreground">
            {t('invites.expires', { date: new Date(newInvite.expiresAt).toLocaleDateString() })}
          </p>
        </div>
      )}

      <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
        {createMutation.isPending ? t('common:creating') : t('invites.createInvite')}
      </Button>

      {createMutation.isError && (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription>{t('invites.failedToCreate')}</AlertDescription>
        </Alert>
      )}
    </Tile>
  );
}

function UploadPermissionsSection({
  familyId,
  minUploadRole,
  members,
}: {
  familyId: string;
  minUploadRole: string;
  members: Member[];
}) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const settingsMutation = useMutation({
    mutationFn: (newRole: string) =>
      apiClient.put(`/api/families/${familyId}/upload-settings`, { minUploadRole: newRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
    },
  });

  const memberUploadMutation = useMutation({
    mutationFn: ({ memberId, canUpload }: { memberId: string; canUpload: string | null }) =>
      apiClient.put(`/api/families/${familyId}/members/${memberId}`, { canUpload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'members'] });
    },
  });

  // Non-owner/admin members who can have upload overrides
  const overridableMembers = members.filter(m => m.role !== 'owner' && m.role !== 'admin');

  return (
    <Tile variant="white" className="p-5">
      <h3 className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
        {t('uploadPermissions.title')}
      </h3>

      <div className="space-y-4">
        {/* Family-level default */}
        <div>
          <label htmlFor="min-upload-role" className="block text-sm text-foreground mb-1.5">
            {t('uploadPermissions.minRole')}
          </label>
          <Select
            id="min-upload-role"
            value={minUploadRole}
            onChange={(e) => settingsMutation.mutate(e.target.value)}
            disabled={settingsMutation.isPending}
            aria-label={t('uploadPermissions.minRole')}
          >
            <option value="viewer">{t('uploadPermissions.viewerAnyone')}</option>
            <option value="contributor">{t('uploadPermissions.contributorDefault')}</option>
            <option value="admin">{t('uploadPermissions.admin')}</option>
            <option value="owner">{t('uploadPermissions.ownerOnly')}</option>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('uploadPermissions.ownersAdminsAlways')}
          </p>
        </div>

        {/* Per-member overrides */}
        {overridableMembers.length > 0 && (
          <div>
            <p className="text-sm text-foreground mb-2">{t('uploadPermissions.perMemberOverrides')}</p>
            <div className="space-y-2">
              {overridableMembers.map((member) => {
                const uploadStatus = member.canUpload === 'true' ? 'allow' : member.canUpload === 'false' ? 'block' : 'default';
                return (
                  <div key={member.id} className="flex items-center justify-between rounded-xl bg-[#faf8f5] dark:bg-zinc-800/50 p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{member.user.name}</p>
                      <p className="text-xs text-muted-foreground">{member.role}</p>
                    </div>
                    <Select
                      value={uploadStatus}
                      onChange={(e) => {
                        const val = e.target.value;
                        const canUpload = val === 'allow' ? 'true' : val === 'block' ? 'false' : null;
                        memberUploadMutation.mutate({ memberId: member.id, canUpload });
                      }}
                      disabled={memberUploadMutation.isPending}
                      className="h-8 text-xs"
                      aria-label={t('uploadPermissions.permissionFor', { name: member.user.name })}
                    >
                      <option value="default">{t('uploadPermissions.useDefault')}</option>
                      <option value="allow">{t('uploadPermissions.allowUpload')}</option>
                      <option value="block">{t('uploadPermissions.blockUpload')}</option>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
}

function FullAccessSection({ familyId, members }: { familyId: string; members: Member[] }) {
  const { t } = useTranslation('settings');
  const { data: groupsData } = useAccessGroups(familyId);
  const group = groupsData?.accessGroups?.[0];
  const { data: groupMembersData } = useAccessGroupMembers(familyId, group?.id);
  const setMembersMutation = useSetAccessGroupMembers(familyId, group?.id);

  const groupMemberIds = new Set((groupMembersData?.members ?? []).map((m: { userId: string }) => m.userId));

  const handleToggle = (userId: string, checked: boolean) => {
    const current = [...groupMemberIds];
    const newIds = checked ? [...current, userId] : current.filter(id => id !== userId);
    setMembersMutation.mutate(newIds);
  };

  if (!group) return null;

  const ownerAdminIds = new Set(members.filter(m => m.role === 'owner' || m.role === 'admin').map(m => m.userId));

  return (
    <Tile variant="white" className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
          {t('fullAccess.title')}
        </h3>
      </div>

      <p className="text-sm text-muted-foreground mb-3">
        {t('fullAccess.description')}
      </p>

      <div className="space-y-2">
        {members.map((member) => {
          const isRequired = ownerAdminIds.has(member.userId);
          const isInGroup = groupMemberIds.has(member.userId);

          return (
            <label
              key={member.userId}
              className="flex items-center gap-2.5 rounded-xl bg-[#faf8f5] dark:bg-zinc-800/50 p-3 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={isRequired || isInGroup}
                disabled={isRequired || setMembersMutation.isPending}
                onChange={(e) => handleToggle(member.userId, e.target.checked)}
                className="rounded border-muted-foreground"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{member.user.name}</p>
                <p className="text-xs text-muted-foreground">{member.role}</p>
              </div>
              {isRequired && (
                <span className="text-xs text-muted-foreground">{t('fullAccess.required')}</span>
              )}
            </label>
          );
        })}
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        {t('fullAccess.summary', { count: Math.max(groupMemberIds.size, ownerAdminIds.size), total: members.length })}
      </p>
    </Tile>
  );
}

function DownloadPermissionsSection({
  familyId,
  allowDownloads,
  members,
}: {
  familyId: string;
  allowDownloads: string;
  members: Member[];
}) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const settingsMutation = useMutation({
    mutationFn: (newVal: string) =>
      apiClient.put(`/api/families/${familyId}/download-settings`, { allowDownloads: newVal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
    },
  });

  const memberDownloadMutation = useMutation({
    mutationFn: ({ memberId, canDownload }: { memberId: string; canDownload: string | null }) =>
      apiClient.put(`/api/families/${familyId}/members/${memberId}`, { canDownload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId, 'members'] });
    },
  });

  const overridableMembers = members.filter(m => m.role !== 'owner' && m.role !== 'admin');

  return (
    <Tile variant="white" className="p-5">
      <h3 className="font-mono text-xs uppercase tracking-wide text-muted-foreground mb-3">
        {t('downloadPermissions.title')}
      </h3>

      <div className="space-y-4">
        <div>
          <label htmlFor="allow-downloads" className="block text-sm text-foreground mb-1.5">
            {t('downloadPermissions.allowDownloads')}
          </label>
          <Select
            id="allow-downloads"
            value={allowDownloads}
            onChange={(e) => settingsMutation.mutate(e.target.value)}
            disabled={settingsMutation.isPending}
            aria-label={t('downloadPermissions.allowDownloads')}
          >
            <option value="true">{t('downloadPermissions.yesDefault')}</option>
            <option value="false">{t('downloadPermissions.no')}</option>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('downloadPermissions.ownersAdminsAlways')}
          </p>
        </div>

        {overridableMembers.length > 0 && (
          <div>
            <p className="text-sm text-foreground mb-2">{t('downloadPermissions.perMemberOverrides')}</p>
            <div className="space-y-2">
              {overridableMembers.map((member) => {
                const downloadStatus = member.canDownload === 'true' ? 'allow' : member.canDownload === 'false' ? 'block' : 'default';
                return (
                  <div key={member.id} className="flex items-center justify-between rounded-xl bg-[#faf8f5] dark:bg-zinc-800/50 p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{member.user.name}</p>
                      <p className="text-xs text-muted-foreground">{member.role}</p>
                    </div>
                    <Select
                      value={downloadStatus}
                      onChange={(e) => {
                        const val = e.target.value;
                        const canDownload = val === 'allow' ? 'true' : val === 'block' ? 'false' : null;
                        memberDownloadMutation.mutate({ memberId: member.id, canDownload });
                      }}
                      disabled={memberDownloadMutation.isPending}
                      className="h-8 text-xs"
                      aria-label={t('downloadPermissions.permissionFor', { name: member.user.name })}
                    >
                      <option value="default">{t('downloadPermissions.useDefault')}</option>
                      <option value="allow">{t('downloadPermissions.allowDownload')}</option>
                      <option value="block">{t('downloadPermissions.blockDownload')}</option>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
}
